/** Validated keyboard-accessible assignment control for one Pitch player. */
import React, { memo, useCallback, useMemo } from "react";

import type { TacticalSlot } from "@/generated/graphql";

import PitchAssignmentSelectView, {
  type PitchAssignmentSurface,
  type PitchAssignmentTriggerLayout,
} from "./PitchAssignmentSelectView";

/**
 * Trigger layout per surface (SE-8442): the card already names its slot, so
 * its trigger is a bare chevron; the members panel row is 280 px wide, so its
 * trigger is capped; the Unassigned panel has room for the full one.
 */
const SURFACE_LAYOUT: Readonly<
  Record<PitchAssignmentSurface, PitchAssignmentTriggerLayout>
> = {
  members: "narrow",
  pitch: "compact",
  unassigned: "full",
};

/** Accessible origin per surface. */
const SURFACE_ORIGIN: Readonly<Record<PitchAssignmentSurface, string>> = {
  members: "members panel",
  pitch: "pitch",
  unassigned: "the Unassigned panel",
};

/** Public props for an explicit player assignment control. */
export interface PitchAssignmentSelectProps {
  readonly surface: PitchAssignmentSurface;
  readonly playerId: string;
  readonly playerName: string;
  readonly value: TacticalSlot | null;
  readonly slots: readonly TacticalSlot[];
  /** Trigger text while no slot is selected; "Unassigned" by default. */
  readonly triggerLabel?: string;
  /** Whether the Unassigned option is offered; true by default. */
  readonly includeUnassigned?: boolean;
  /** Read-only trigger (SE-8391: a viewer, or a row with nowhere to go). */
  readonly isDisabled?: boolean;
  readonly onAssign: (
    playerId: string,
    tacticalSlot: TacticalSlot | null
  ) => void;
}

/**
 * Converts Select values into a validated assignment request.
 * @param props - Player identity, current formation slots, and callback.
 * @param props.playerId - Player receiving the assignment.
 * @param props.surface - Surface that owns the unique Select identity.
 * @param props.playerName - Accessible player label.
 * @param props.value - Current effective tactical slot.
 * @param props.slots - Slots offered in the current formation.
 * @param props.triggerLabel - Trigger text while nothing is selected.
 * @param props.includeUnassigned - Whether the Unassigned option is offered.
 * @param props.isDisabled - Whether the trigger is read-only.
 * @param props.onAssign - Receives an authorized assignment request.
 */
const PitchAssignmentSelectContainer = ({
  playerId,
  surface,
  playerName,
  value,
  slots,
  triggerLabel,
  includeUnassigned,
  isDisabled,
  onAssign,
}: PitchAssignmentSelectProps) => {
  const accessibleLabel = useMemo(
    () =>
      `Assign ${playerName} from ${SURFACE_ORIGIN[surface]} to a pitch position`,
    [playerName, surface]
  );
  const handleChange = useCallback(
    (nextValue: string) => {
      if (nextValue === "UNASSIGNED") {
        if (includeUnassigned !== false) onAssign(playerId, null);
        return;
      }
      if (slots.includes(nextValue as TacticalSlot)) {
        onAssign(playerId, nextValue as TacticalSlot);
      }
    },
    [includeUnassigned, onAssign, playerId, slots]
  );
  return (
    <PitchAssignmentSelectView
      playerId={playerId}
      surface={surface}
      accessibleLabel={accessibleLabel}
      value={value}
      slots={slots}
      triggerLabel={triggerLabel}
      includeUnassigned={includeUnassigned}
      isDisabled={isDisabled}
      layout={SURFACE_LAYOUT[surface]}
      onChange={handleChange}
    />
  );
};

PitchAssignmentSelectContainer.displayName = "PitchAssignmentSelectContainer";
export default memo(PitchAssignmentSelectContainer);
